/**
 * <MemorialSupportPanel /> — the "Support this memorial" card that lives on
 * /celebrate/:slug. Users can light a candle, send flowers, make a donation,
 * order a photo book, buy a livestream, or subscribe to anniversary
 * postcards. Every option round-trips through Stripe Checkout (Flow B).
 *
 * The catalog is fetched from the backend so amounts + labels are never
 * hard-coded on the client.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Flame, Flower, Heart, BookOpen, Video, Send, Loader2 } from "lucide-react";
import L from "@/components/L";

const KIND_META = {
  candle:     { Icon: Flame,    color: "#F97316", section: "Light a candle" },
  flowers:    { Icon: Flower,   color: "#DB2777", section: "Send flowers" },
  donation:   { Icon: Heart,    color: "#B25D38", section: "Donate in memory" },
  photobook:  { Icon: BookOpen, color: "#7C3AED", section: "Printed photo book" },
  livestream: { Icon: Video,    color: "#0891B2", section: "Funeral livestream" },
  postcards:  { Icon: Send,     color: "#059669", section: "Anniversary postcards (yearly)" },
};
const KIND_ORDER = ["candle", "flowers", "donation", "photobook", "livestream", "postcards"];

export default function MemorialSupportPanel({ memorial }) {
  const [catalog, setCatalog] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [donorMessage, setDonorMessage] = useState("");
  const [showMessageField, setShowMessageField] = useState(false);

  useEffect(() => {
    http.get("/memorials/checkout/catalog")
      .then((r) => setCatalog(r.data.items || []))
      .catch(() => setCatalog([]));
  }, []);

  const grouped = KIND_ORDER
    .map((kind) => ({ kind, meta: KIND_META[kind], items: catalog.filter((c) => c.kind === kind) }))
    .filter((g) => g.items.length > 0);

  const start = async (lookup_key) => {
    if (!memorial?.slug && !memorial?.id) return;
    setBusyKey(lookup_key);
    try {
      const { data } = await http.post(`/memorials/${memorial.slug || memorial.id}/checkout`, {
        lookup_key,
        origin_url: window.location.origin,
        donor_message: donorMessage.trim() || undefined,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Couldn't create checkout session.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="wofy-card mt-6" data-testid="memorial-support-panel">
      <div className="overline" style={{ color: "var(--wofy-primary)" }}>
        <Heart size={12} className="inline -mt-0.5 mr-1" /> SUPPORT & REMEMBRANCE
      </div>
      <h2 className="font-display text-2xl mt-1"><L>Ways to honour {memorial?.full_name?.split(" ")[0] || "them"}</L></h2>
      <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
        Every contribution is processed securely by Stripe. Your name and message (if provided)
        will appear on the memorial's wall of tributes.
      </p>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setShowMessageField((v) => !v)}
          className="text-xs underline"
          style={{ color: "var(--wofy-primary)" }}
          data-testid="memorial-support-toggle-message"
        >
          {showMessageField ? "Hide message field" : "Add a personal message (optional)"}
        </button>
        {showMessageField && (
          <textarea
            className="wofy-input w-full mt-2 text-sm"
            rows={3}
            maxLength={1000}
            placeholder="Share a memory or note that will appear beside your tribute…"
            value={donorMessage}
            onChange={(e) => setDonorMessage(e.target.value)}
            data-testid="memorial-support-message"
          />
        )}
      </div>

      <div className="mt-6 space-y-6">
        {grouped.map(({ kind, meta, items }) => {
          const { Icon, color, section } = meta;
          return (
            <div key={kind} data-testid={`memorial-support-section-${kind}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color }} />
                <div className="overline">{section}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {items.map((it) => (
                  <button
                    key={it.lookup_key}
                    type="button"
                    onClick={() => start(it.lookup_key)}
                    disabled={busyKey === it.lookup_key}
                    className="wofy-btn-ghost border text-sm"
                    style={{ borderColor: color, color }}
                    data-testid={`memorial-support-btn-${it.lookup_key}`}
                  >
                    {busyKey === it.lookup_key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                    <span>{it.label}</span>
                    <span className="font-mono font-semibold ml-1">${it.amount.toFixed(0)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] mt-6 opacity-60">
        Secure payments powered by Stripe · Every payment is logged on this memorial's tribute wall.
      </p>
    </div>
  );
}
